import React, { useState, useEffect, useRef } from "react";
import { Input, Button } from "antd";
import { GiftOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { ArrowRight, Sparkles, Award, UploadCloud, CheckCircle2, Camera, Check, Users } from "lucide-react";
import { _apply_as_ambassador, _upload_image } from "../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";
import {
  cleanPhoneForInput,
  validatePhoneNumber,
  formatFullPhone,
} from "../../utils/phoneHelpers";

const GRADUATION_YEARS = ["2027", "2028", "2029", "2030", "2031", "2032"];

const resolveInitialFormState = (userId, userProfile) => {
  let draft = null;
  if (userId) {
    try {
      const saved = localStorage.getItem(`konnect_ambassador_form_draft_${userId}`);
      if (saved) {
        draft = JSON.parse(saved);
      }
    } catch (e) {}
  }

  const hasExistingApplication = Boolean(
    userProfile?.hasApplied ||
    userProfile?.isAmbassador ||
    (userProfile?.status && userProfile.status !== "NOT_APPLIED") ||
    (userProfile?.ambassadorStatus && userProfile.ambassadorStatus !== "NOT_APPLIED") ||
    (userProfile?.ambassadorProfile?.status && userProfile.ambassadorProfile.status !== "NOT_APPLIED")
  );

  const phoneCode = draft?.phoneCode || userProfile?.phoneCode || "+234";

  let phoneNumber = "";
  if (draft?.phoneNumber !== undefined && draft?.phoneNumber !== null && draft?.phoneNumber !== "") {
    phoneNumber = draft.phoneNumber;
  } else if (userProfile?.phoneNumber) {
    phoneNumber = cleanPhoneForInput(userProfile.phoneNumber, phoneCode);
  }

  let whatsappNumber = "";
  let sameAsTelephone = false;
  if (draft?.whatsappNumber !== undefined && draft?.whatsappNumber !== null && draft?.whatsappNumber !== "") {
    whatsappNumber = draft.whatsappNumber;
    sameAsTelephone = Boolean(draft.sameAsTelephone);
  } else {
    const rawWhatsapp = userProfile?.ambassadorWhatsapp || "";
    const cleanedWhatsapp = cleanPhoneForInput(rawWhatsapp, phoneCode);
    const rawTelephone = userProfile?.phoneNumber || "";
    const cleanedTelephone = cleanPhoneForInput(rawTelephone, phoneCode);
    if (cleanedWhatsapp) {
      whatsappNumber = cleanedWhatsapp;
      sameAsTelephone = cleanedWhatsapp === cleanedTelephone;
    } else if (cleanedTelephone) {
      whatsappNumber = cleanedTelephone;
      sameAsTelephone = true;
    }
  }

  let igUrl = "";
  if (draft?.igUrl !== undefined && draft?.igUrl !== null) {
    igUrl = draft.igUrl;
  } else if (userProfile?.igUrl || userProfile?.ambassadorIgUrl) {
    igUrl = userProfile.igUrl || userProfile.ambassadorIgUrl || "";
  }

  let tiktokUrl = "";
  if (draft?.tiktokUrl !== undefined && draft?.tiktokUrl !== null) {
    tiktokUrl = draft.tiktokUrl;
  } else if (userProfile?.tiktokUrl || userProfile?.ambassadorTiktokUrl) {
    tiktokUrl = userProfile.tiktokUrl || userProfile.ambassadorTiktokUrl || "";
  }

  let facebookUrl = "";
  if (draft?.facebookUrl !== undefined && draft?.facebookUrl !== null) {
    facebookUrl = draft.facebookUrl;
  } else if (userProfile?.facebookUrl || userProfile?.ambassadorFacebookUrl) {
    facebookUrl = userProfile.facebookUrl || userProfile.ambassadorFacebookUrl || "";
  }

  let followerRange = null;
  if (draft?.followerRange !== undefined && draft?.followerRange !== null) {
    followerRange = draft.followerRange;
  } else if (userProfile?.followersRange || userProfile?.ambassadorFollowersRange) {
    const val = userProfile.followersRange || userProfile.ambassadorFollowersRange;
    if (val.includes("1000") || val.includes("1,000")) followerRange = "1000-3000";
    else if (val.includes("3000") || val.includes("3,000")) followerRange = "3000-6000";
    else if (val.includes("6000") || val.includes("6,000")) followerRange = "6000+";
    else followerRange = val;
  }

  let isStudent = false;
  if (draft?.isStudent !== undefined) {
    isStudent = Boolean(draft.isStudent);
  } else if (userProfile) {
    const isStud =
      userProfile.isStudent !== undefined && userProfile.isStudent !== null
        ? (userProfile.isStudent === true || String(userProfile.isStudent) === "1" || String(userProfile.isStudent) === "true")
        : (userProfile.ambassadorIsStudent !== undefined && userProfile.ambassadorIsStudent !== null
          ? (userProfile.ambassadorIsStudent === true || String(userProfile.ambassadorIsStudent) === "1" || String(userProfile.ambassadorIsStudent) === "true")
          : Boolean(userProfile.school || userProfile.ambassadorSchool));
    if (isStud) isStudent = true;
  }

  let school = "";
  if (draft?.school) {
    school = draft.school;
  } else {
    school = userProfile?.school || userProfile?.ambassadorSchool || "";
  }

  let customSchool = draft?.customSchool || "";

  let graduationYear = "";
  if (draft?.graduationYear) {
    graduationYear = String(draft.graduationYear);
  } else {
    const gradYear = userProfile?.graduationYear || userProfile?.ambassadorGraduationYear;
    if (gradYear) graduationYear = String(gradYear);
  }

  let studentNumber = "";
  if (draft?.studentNumber) {
    studentNumber = String(draft.studentNumber);
  } else {
    const studNum = userProfile?.studentNumber || userProfile?.ambassadorStudentNumber;
    if (studNum) studentNumber = String(studNum);
  }

  let recentPictureUrl = "";
  if (draft?.recentPictureUrl) {
    recentPictureUrl = draft.recentPictureUrl;
  } else {
    recentPictureUrl = userProfile?.recentPictureUrl || userProfile?.ambassadorRecentPictureUrl || "";
  }

  let studentIdCardUrl = "";
  if (draft?.studentIdCardUrl) {
    studentIdCardUrl = draft.studentIdCardUrl;
  } else {
    studentIdCardUrl = userProfile?.studentIdCardUrl || userProfile?.ambassadorStudentIdCardUrl || "";
  }

  let topPosts = [""];
  if (Array.isArray(draft?.topPosts) && draft.topPosts.length > 0) {
    topPosts = draft.topPosts;
  } else {
    const posts = userProfile?.topPosts || userProfile?.ambassadorTopPosts;
    if (Array.isArray(posts) && posts.length > 0) {
      topPosts = posts;
    }
  }

  let currentStep = draft?.currentStep || 1;

  let hasSocialPresence = null;
  if (draft?.hasSocialPresence !== undefined && draft?.hasSocialPresence !== null) {
    hasSocialPresence = draft.hasSocialPresence;
  } else if (userProfile?.hasSocialPresence !== undefined && userProfile?.hasSocialPresence !== null) {
    hasSocialPresence = userProfile.hasSocialPresence;
  } else if (hasExistingApplication) {
    hasSocialPresence = true;
  }

  let onboardRange = null;
  if (draft?.onboardRange !== undefined && draft?.onboardRange !== null) {
    onboardRange = draft.onboardRange === "5 & above businesses" ? "5+" : draft.onboardRange;
  } else if (userProfile?.onboardRange) {
    onboardRange = userProfile.onboardRange === "5 & above businesses" ? "5+" : userProfile.onboardRange;
  } else if (hasExistingApplication) {
    onboardRange = "5+";
  }

  let hasTimeDaily = null;
  if (draft?.hasTimeDaily !== undefined && draft?.hasTimeDaily !== null) {
    hasTimeDaily = draft.hasTimeDaily;
  } else if (userProfile?.hasTimeDaily !== undefined && userProfile?.hasTimeDaily !== null) {
    hasTimeDaily = userProfile.hasTimeDaily;
  } else if (hasExistingApplication) {
    hasTimeDaily = true;
  }

  let agreedToTerms = false;
  if (draft?.agreedToTerms !== undefined && draft?.agreedToTerms !== null) {
    agreedToTerms = Boolean(draft.agreedToTerms);
  } else if (userProfile?.agreedToTerms !== undefined && userProfile?.agreedToTerms !== null) {
    agreedToTerms = Boolean(userProfile.agreedToTerms);
  } else if (hasExistingApplication) {
    agreedToTerms = true;
  }

  return {
    phoneCode,
    phoneNumber,
    whatsappNumber,
    sameAsTelephone,
    igUrl,
    tiktokUrl,
    facebookUrl,
    followerRange,
    isStudent,
    school,
    customSchool,
    graduationYear,
    studentNumber,
    recentPictureUrl,
    studentIdCardUrl,
    topPosts,
    currentStep,
    hasSocialPresence,
    onboardRange,
    hasTimeDaily,
    agreedToTerms,
  };
};

const AmbassadorApplicationForm = ({
  userProfile,
  schoolsList = [],
  onSuccess,
  onOpenTerms,
  agreedToTerms: propAgreedToTerms,
  setAgreedToTerms: propSetAgreedToTerms,
}) => {
  const userId = userProfile?.id || userProfile?.accountId || "user";
  const hasExistingApplication = Boolean(
    userProfile?.hasApplied ||
    userProfile?.isAmbassador ||
    (userProfile?.status && userProfile.status !== "NOT_APPLIED") ||
    (userProfile?.ambassadorStatus && userProfile.ambassadorStatus !== "NOT_APPLIED") ||
    (userProfile?.ambassadorProfile?.status && userProfile.ambassadorProfile.status !== "NOT_APPLIED")
  );

  const [initialValues] = useState(() => resolveInitialFormState(userId, userProfile));

  const [currentStep, setCurrentStep] = useState(initialValues.currentStep);
  const [submitting, setSubmitting] = useState(false);

  const pictureInputRef = useRef(null);
  const idCardInputRef = useRef(null);

  const [phoneNumber, setPhoneNumber] = useState(initialValues.phoneNumber);
  const [whatsappNumber, setWhatsappNumber] = useState(initialValues.whatsappNumber);
  const [sameAsTelephone, setSameAsTelephone] = useState(initialValues.sameAsTelephone);
  const [phoneCode, setPhoneCode] = useState(initialValues.phoneCode);
  const [isStudent, setIsStudent] = useState(initialValues.isStudent);
  const [school, setSchool] = useState(initialValues.school);
  const [customSchool, setCustomSchool] = useState(initialValues.customSchool);
  const [graduationYear, setGraduationYear] = useState(initialValues.graduationYear);
  const [studentNumber, setStudentNumber] = useState(initialValues.studentNumber);
  const [recentPictureUrl, setRecentPictureUrl] = useState(initialValues.recentPictureUrl);
  const [studentIdCardUrl, setStudentIdCardUrl] = useState(initialValues.studentIdCardUrl);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingIdCard, setUploadingIdCard] = useState(false);

  const [igUrl, setIgUrl] = useState(initialValues.igUrl);
  const [tiktokUrl, setTiktokUrl] = useState(initialValues.tiktokUrl);
  const [facebookUrl, setFacebookUrl] = useState(initialValues.facebookUrl);
  const [followerRange, setFollowerRange] = useState(initialValues.followerRange);
  const [topPosts, setTopPosts] = useState(initialValues.topPosts);

  const [hasSocialPresence, setHasSocialPresence] = useState(initialValues.hasSocialPresence);
  const [onboardRange, setOnboardRange] = useState(initialValues.onboardRange);
  const [hasTimeDaily, setHasTimeDaily] = useState(initialValues.hasTimeDaily);
  const [localAgreedToTerms, setLocalAgreedToTerms] = useState(initialValues.agreedToTerms);
  const agreedToTerms = propAgreedToTerms !== undefined ? (propAgreedToTerms || localAgreedToTerms) : localAgreedToTerms;
  const setAgreedToTerms = (val) => {
    setLocalAgreedToTerms(val);
    if (propSetAgreedToTerms) {
      propSetAgreedToTerms(val);
    }
  };

  const initializedUserIdRef = useRef(userId);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (initialValues.agreedToTerms && propSetAgreedToTerms && !propAgreedToTerms) {
      propSetAgreedToTerms(true);
    }
  }, []);

  useEffect(() => {
    if (userId && initializedUserIdRef.current !== userId) {
      initializedUserIdRef.current = userId;
      const reloaded = resolveInitialFormState(userId, userProfile);
      setCurrentStep(reloaded.currentStep);
      setPhoneNumber(reloaded.phoneNumber);
      setWhatsappNumber(reloaded.whatsappNumber);
      setSameAsTelephone(reloaded.sameAsTelephone);
      setPhoneCode(reloaded.phoneCode);
      setIsStudent(reloaded.isStudent);
      setSchool(reloaded.school);
      setCustomSchool(reloaded.customSchool);
      setGraduationYear(reloaded.graduationYear);
      setStudentNumber(reloaded.studentNumber);
      setRecentPictureUrl(reloaded.recentPictureUrl);
      setStudentIdCardUrl(reloaded.studentIdCardUrl);
      setIgUrl(reloaded.igUrl);
      setTiktokUrl(reloaded.tiktokUrl);
      setFacebookUrl(reloaded.facebookUrl);
      setFollowerRange(reloaded.followerRange);
      setTopPosts(reloaded.topPosts);
      setHasSocialPresence(reloaded.hasSocialPresence);
      setOnboardRange(reloaded.onboardRange);
      setHasTimeDaily(reloaded.hasTimeDaily);
      setLocalAgreedToTerms(reloaded.agreedToTerms);
      if (propSetAgreedToTerms && reloaded.agreedToTerms) {
        propSetAgreedToTerms(true);
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (!userId) return;
    try {
      const draft = {
        currentStep,
        hasSocialPresence,
        onboardRange,
        hasTimeDaily,
        agreedToTerms,
        phoneNumber,
        whatsappNumber,
        sameAsTelephone,
        phoneCode,
        isStudent,
        school,
        customSchool,
        graduationYear,
        studentNumber,
        recentPictureUrl,
        studentIdCardUrl,
        igUrl,
        tiktokUrl,
        facebookUrl,
        followerRange,
        topPosts,
      };
      localStorage.setItem(`konnect_ambassador_form_draft_${userId}`, JSON.stringify(draft));
    } catch (e) {}
  }, [
    userId,
    currentStep,
    hasSocialPresence,
    onboardRange,
    hasTimeDaily,
    agreedToTerms,
    phoneNumber,
    whatsappNumber,
    sameAsTelephone,
    phoneCode,
    isStudent,
    school,
    customSchool,
    graduationYear,
    studentNumber,
    recentPictureUrl,
    studentIdCardUrl,
    igUrl,
    tiktokUrl,
    facebookUrl,
    followerRange,
    topPosts,
  ]);

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === "picture") setUploadingPicture(true);
    if (type === "idcard") setUploadingIdCard(true);

    try {
      const res = await _upload_image(file);
      const url =
        res.data?.url ||
        res.data?.data?.url ||
        res.data?.imageUrl ||
        (typeof res.data === "string" ? res.data : "");
      if (url) {
        if (type === "picture") setRecentPictureUrl(url);
        if (type === "idcard") setStudentIdCardUrl(url);
        showSuccessNotification("File uploaded successfully!");
      } else {
        showErrorNotification("Failed to upload file.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Upload failed.");
    } finally {
      if (type === "picture") setUploadingPicture(false);
      if (type === "idcard") setUploadingIdCard(false);
    }
  };

  const validateSocialUrl = (url, platformName) => {
    if (!url || !url.trim()) return true;
    const val = url.trim().toLowerCase();
    if (!val.startsWith("https://")) {
      showErrorNotification(`${platformName} URL must start with https://`);
      return false;
    }
    if (platformName === "Instagram" && !val.includes("instagram.com")) {
      showErrorNotification(
        "Instagram URL must contain instagram.com (e.g. https://www.instagram.com/yourusername)"
      );
      return false;
    }
    if (platformName === "TikTok" && !val.includes("tiktok.com")) {
      showErrorNotification(
        "TikTok URL must contain tiktok.com (e.g. https://www.tiktok.com/@yourusername)"
      );
      return false;
    }
    if (platformName === "Facebook" && !val.includes("facebook.com") && !val.includes("fb.com")) {
      showErrorNotification("Facebook URL must contain facebook.com");
      return false;
    }
    return true;
  };

  const validateStep1 = () => {
    if (!phoneNumber || !phoneNumber.trim()) {
      showErrorNotification("Please enter a valid telephone number.");
      return false;
    }
    if (!validatePhoneNumber(phoneNumber)) {
      showErrorNotification("Please enter a valid telephone number (7 to 15 digits).");
      return false;
    }
    const effectiveWhatsapp = sameAsTelephone ? phoneNumber : whatsappNumber;
    if (!effectiveWhatsapp || !effectiveWhatsapp.trim()) {
      showErrorNotification("Please enter a valid WhatsApp number.");
      return false;
    }
    if (!validatePhoneNumber(effectiveWhatsapp)) {
      showErrorNotification("Please enter a valid WhatsApp number (7 to 15 digits).");
      return false;
    }
    if (isStudent) {
      const effectiveSchool = school === "Other" ? customSchool : school;
      if (!effectiveSchool || !effectiveSchool.trim()) {
        showErrorNotification("Please select or specify your School/University.");
        return false;
      }
      if (!graduationYear) {
        showErrorNotification("Please select your Graduation Year.");
        return false;
      }
      if (!studentNumber || !studentNumber.trim()) {
        showErrorNotification("Please enter your Student Number.");
        return false;
      }
      if (!recentPictureUrl) {
        showErrorNotification("Please upload a Recent Picture.");
        return false;
      }
      if (!studentIdCardUrl) {
        showErrorNotification("Please upload your Student ID Card.");
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    if (!igUrl.trim() && !tiktokUrl.trim() && !facebookUrl.trim()) {
      showErrorNotification(
        "Please provide at least one social media profile link (Instagram, TikTok, or Facebook)."
      );
      return false;
    }
    if (!validateSocialUrl(igUrl, "Instagram")) return false;
    if (!validateSocialUrl(tiktokUrl, "TikTok")) return false;
    if (!validateSocialUrl(facebookUrl, "Facebook")) return false;
    if (!followerRange) {
      showErrorNotification("Please select your total followers range.");
      return false;
    }
    const validTopPosts = topPosts.filter((p) => p.trim() !== "");
    if (validTopPosts.length === 0) {
      showErrorNotification("Please provide at least one link to your top performing post.");
      return false;
    }
    for (let i = 0; i < topPosts.length; i++) {
      const link = topPosts[i].trim();
      if (link && !link.toLowerCase().startsWith("https://")) {
        showErrorNotification(`Top post #${i + 1} link must start with https://`);
        return false;
      }
    }
    return true;
  };

  const validateStep3 = () => {
    if (hasSocialPresence === null && !hasExistingApplication) {
      showErrorNotification("Please answer if you have a strong social media presence.");
      return false;
    }
    if (onboardRange === null && !hasExistingApplication) {
      showErrorNotification("Please select how many businesses you can onboard monthly.");
      return false;
    }
    if (hasTimeDaily === null && !hasExistingApplication) {
      showErrorNotification("Please answer if you have time to post daily.");
      return false;
    }
    if (!agreedToTerms && !hasExistingApplication) {
      showErrorNotification("You must read and agree to the Ambassador agreements & terms.");
      return false;
    }
    return true;
  };

  const handleNextStep1 = () => {
    if (!validateStep1()) return;
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNextStep2 = () => {
    if (!validateStep2()) return;
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePostLinkChange = (index, value) => {
    const updated = [...topPosts];
    updated[index] = value;
    setTopPosts(updated);
  };

  const handleAddPostLink = () => {
    if (topPosts.length >= 5) {
      showErrorNotification("Maximum of 5 top post links allowed.");
      return;
    }
    setTopPosts([...topPosts, ""]);
  };

  const handleRemovePostLink = (index) => {
    if (topPosts.length > 1) {
      setTopPosts(topPosts.filter((_, i) => i !== index));
    }
  };

  const handleApply = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!validateStep2()) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!validateStep3()) return;

    setSubmitting(true);
    try {
      const effectiveWhatsapp = sameAsTelephone ? phoneNumber : whatsappNumber;
      const formattedWhatsapp = formatFullPhone(phoneCode, effectiveWhatsapp);
      const cleanTelephone = cleanPhoneForInput(phoneNumber, phoneCode);
      const effectiveSchool = school === "Other" ? customSchool.trim() : school;
      const payload = {
        phoneNumber: cleanTelephone,
        phoneCode,
        whatsappNumber: formattedWhatsapp,
        igUrl: igUrl.trim(),
        tiktokUrl: tiktokUrl.trim(),
        facebookUrl: facebookUrl.trim(),
        followersRange: followerRange,
        topPosts: topPosts.filter((p) => p.trim() !== ""),
        isStudent,
        school: effectiveSchool,
        graduationYear,
        studentNumber: studentNumber.trim(),
        recentPictureUrl,
        studentIdCardUrl,
        hasSocialPresence,
        onboardRange,
        hasTimeDaily,
        agreedToTerms,
      };
      const res = await _apply_as_ambassador(payload);
      if (res.data?.success || res.status === 200 || res.status === 201) {
        isMountedRef.current = false;
        if (userId) {
          const submittedState = {
            currentStep,
            hasSocialPresence,
            onboardRange,
            hasTimeDaily,
            agreedToTerms: true,
            phoneNumber,
            whatsappNumber,
            sameAsTelephone,
            phoneCode,
            isStudent,
            school,
            customSchool,
            graduationYear,
            studentNumber,
            recentPictureUrl,
            studentIdCardUrl,
            igUrl,
            tiktokUrl,
            facebookUrl,
            followerRange,
            topPosts,
            isSubmitted: true,
          };
          try {
            localStorage.setItem(
              `konnect_ambassador_form_draft_${userId}`,
              JSON.stringify(submittedState)
            );
          } catch (e) {}
        }
        showSuccessNotification(
          hasExistingApplication
            ? "Ambassador Application updated successfully!"
            : "Successfully submitted Ambassador Application!"
        );
        if (onSuccess) onSuccess();
      } else {
        showErrorNotification(res.data?.message || "Failed to process application");
      }
    } catch (err) {
      showErrorNotification(
        err.response?.data?.message || err.message || "Failed to process application"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <Sparkles className="w-6 h-6 text-yellow-700" />,
      bg: "bg-yellow-50",
      title: "3x Earnings Multiplier",
      desc: "Earn 3x on every marketplace sale & referral cashback.",
    },
    {
      icon: <GiftOutlined className="text-xl text-emerald-700" />,
      bg: "bg-emerald-50",
      title: "Weekly Payouts",
      desc: "Automatic Monday payouts credited directly to your wallet.",
    },
    {
      icon: <Award className="w-6 h-6 text-blue-700" />,
      bg: "bg-blue-50",
      title: "Campus & Promo Badges",
      desc: "Verified ambassador recognition and exclusive brand rewards.",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          {[
            { step: 1, title: "Overview & Contact" },
            { step: 2, title: "Social & Links" },
            { step: 3, title: "Survey & Terms" },
          ].map((s) => (
            <div
              key={s.step}
              onClick={() => {
                if (s.step < currentStep) setCurrentStep(s.step);
              }}
              className={`flex-1 text-center cursor-pointer transition-all ${currentStep === s.step
                ? "text-emerald-700 font-gilroyBold"
                : s.step < currentStep
                  ? "text-emerald-600 font-gilroyBold"
                  : "text-gray-500 font-medium"
                }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <span
                  className={`w-9 h-9 rounded-2xl flex items-center justify-center  font-gilroyBold transition-all ${currentStep === s.step
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25 scale-105"
                    : s.step < currentStep
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-gray-100 text-gray-500"
                    }`}
                >
                  {s.step < currentStep ? "✓" : s.step}
                </span>
              </div>
              <span className="text-xs sm: font-gilroyBold block">{s.title}</span>
            </div>
          ))}
        </div>
      </div>

      {currentStep === 1 && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm text-left">
            <div className="bg-gray-900 w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4">
              <Award size={26} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-gilroyBold text-gray-900 tracking-tight m-0 mb-2">
              Become a Konnect Ambassador
            </h1>
            <p className="text-gray-600 text-[13px] leading-relaxed m-0 mt-1 font-medium">
              Grow the pack and earn weekly with a 3x multiplier on all referral and sales income.
            </p>

            <div className="bg-yellow-50 rounded-xl p-3.5 flex items-center gap-3 mt-4">
              <div className="bg-yellow-200 rounded-full p-1.5 flex items-center justify-center flex-shrink-0">
                <Sparkles size={18} className="text-yellow-700" />
              </div>
              <div>
                <div className=" font-gilroyBold text-gray-900">
                  Weekly Payouts & 3x Multiplier
                </div>
                <div className="text-[13px] text-gray-600">
                  Commissions credited every Monday directly into your wallet
                </div>
              </div>
            </div>
          </div>

          <div className="-mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="flex gap-3 overflow-x-auto pb-2 pt-0.5 snap-x snap-mandatory scrollbar-hide">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="bg-white border border-gray-100 rounded-2xl p-4 shadow-xs text-left flex flex-col justify-between w-[260px] sm:w-[260px] snap-start flex-shrink-0 hover:border-gray-200 transition-all select-none"
                >
                  <div>
                    <div
                      className={`${b.bg} p-2 rounded-xl w-9 h-9 flex items-center justify-center`}
                    >
                      {b.icon}
                    </div>
                    <h3 className=" font-gilroyBold text-gray-900 m-0 mt-2.5 mb-1 leading-snug">
                      {b.title}
                    </h3>
                    <p className="text-gray-500 text-[12px] sm:text-[13px] m-0 leading-normal">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6 text-left">
            <div>
              <label className="block text-lg font-gilroyBold text-gray-900 mb-2">
                Telephone Number <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                addonBefore={
                  <span className="text-base font-gilroyBold text-gray-900">
                    {phoneCode}
                  </span>
                }
                placeholder="Enter your telephone number"
                value={phoneNumber}
                onChange={(e) => {
                  const val = e.target.value;
                  setPhoneNumber(val);
                  if (sameAsTelephone) setWhatsappNumber(val);
                }}
                className="rounded-2xl h-14 text-lg font-medium"
              />
              <p className="text-gray-500  m-0 mt-2">
                Primary phone number for calls, SMS OTP, and official account contact.
              </p>
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-lg font-gilroyBold text-gray-900 m-0">
                  Primary WhatsApp Number <span className="text-red-500">*</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer  font-gilroyBold text-emerald-700 select-none">
                  <input
                    type="checkbox"
                    checked={sameAsTelephone}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSameAsTelephone(checked);
                      if (checked) setWhatsappNumber(phoneNumber);
                    }}
                    className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                  />
                  <span>Same as Telephone</span>
                </label>
              </div>
              <Input
                size="large"
                disabled={sameAsTelephone}
                addonBefore={
                  <span className="text-base font-gilroyBold text-gray-900">
                    {phoneCode}
                  </span>
                }
                placeholder="Enter your WhatsApp number"
                value={sameAsTelephone ? phoneNumber : whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className={`rounded-2xl h-14 text-lg font-medium ${sameAsTelephone ? "bg-gray-50" : ""}`}
              />
              <p className="text-gray-500 text-[13px] m-0 mt-1">
                We'll send program updates, announcements, and group access to this number.
              </p>
            </div>

            <div className="bg-gray-50/80 border border-gray-200/80 rounded-2xl p-6 space-y-4 text-left my-4 shadow-xs">
              <div>
                <label className="block text-lg font-gilroyBold text-gray-900 mb-1">
                  Are you currently in a higher institution or university?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <p className="text-[13px] text-gray-600 m-0 mb-3">
                  Student ambassadors unlock verified university badges, campus referral tracks, and fast-track approvals.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setIsStudent(true)}
                  className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all cursor-pointer ${isStudent === true
                    ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                    }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsStudent(false);
                    setSchool("");
                    setCustomSchool("");
                    setGraduationYear("");
                    setStudentNumber("");
                    setRecentPictureUrl("");
                    setStudentIdCardUrl("");
                  }}
                  className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all cursor-pointer ${isStudent === false
                    ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                    }`}
                >
                  No
                </button>
              </div>

              {isStudent && (
                <div className="mt-4 pt-4 border-t border-gray-200/80 space-y-4">
                  <div className="flex items-center gap-2.5 pb-1">
                    <div className="bg-emerald-900 text-white w-7 h-7 rounded-lg flex items-center justify-center text-xs">
                      🎓
                    </div>
                    <h4 className="font-gilroyBold text-base text-gray-900 uppercase tracking-wide m-0">
                      Student Ambassador Details
                    </h4>
                  </div>

                  <div>
                    <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                      School / Higher Institution <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={school}
                      onChange={(e) => setSchool(e.target.value)}
                      className="w-full h-14 rounded-2xl border-2 border-gray-200 px-4 font-gilroyBold text-base bg-white focus:border-emerald-500 outline-none transition-all text-gray-900"
                    >
                      <option value="">Select your School</option>
                      {school && !schoolsList.includes(school) && school !== "Other" && (
                        <option key={school} value={school}>
                          {school}
                        </option>
                      )}
                      {schoolsList
                        .filter((sch) => sch !== "Other")
                        .map((sch) => (
                          <option key={sch} value={sch}>
                            {sch}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                      Graduation Year <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={String(graduationYear || "")}
                      onChange={(e) => setGraduationYear(e.target.value)}
                      className="w-full h-14 rounded-2xl border-2 border-gray-200 px-4 font-gilroyBold text-base bg-white focus:border-emerald-500 outline-none transition-all text-gray-900"
                    >
                      <option value="">Select Graduation Year (2027 to 2032)</option>
                      {graduationYear && !GRADUATION_YEARS.includes(String(graduationYear)) && (
                        <option key={graduationYear} value={String(graduationYear)}>
                          {graduationYear}
                        </option>
                      )}
                      {GRADUATION_YEARS.map((yr) => (
                        <option key={yr} value={yr}>
                          {yr}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                      Student / Matriculation Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      size="large"
                      placeholder="e.g. 123456789 or MAT/2024/001"
                      value={studentNumber}
                      onChange={(e) => setStudentNumber(e.target.value)}
                      className="rounded-2xl h-14 text-base font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div>
                      <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                        Recent Headshot Photo <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        ref={pictureInputRef}
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, "picture")}
                        className="hidden"
                      />
                      {recentPictureUrl ? (
                        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-xs">
                          <div className="flex items-center gap-3 min-w-0">
                            <img
                              src={recentPictureUrl}
                              alt="Recent Picture"
                              className="w-14 h-14 object-cover rounded-xl border border-gray-200 shadow-xs flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <span className="font-gilroyBold  text-gray-900 block truncate">
                                Headshot uploaded
                              </span>
                              <span className="font-gilroyBold text-xs text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full inline-block mt-1">
                                ✓ Current picture verified
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => pictureInputRef.current?.click()}
                            className="text-xs font-gilroyBold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-xl border border-emerald-200 cursor-pointer transition-all flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => pictureInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-200 hover:border-emerald-500 bg-white rounded-2xl p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-1.5"
                        >
                          <UploadCloud className="w-7 h-7 text-emerald-600 mb-0.5" />
                          <span className="font-gilroyBold text-base text-gray-800">
                            {uploadingPicture ? "Uploading photo..." : "Click to upload recent picture"}
                          </span>
                          <span className="text-xs text-gray-500">JPG or PNG up to 5MB</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                        Student ID Card <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        ref={idCardInputRef}
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, "idcard")}
                        className="hidden"
                      />
                      {studentIdCardUrl ? (
                        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-xs">
                          <div className="flex items-center gap-3 min-w-0">
                            <img
                              src={studentIdCardUrl}
                              alt="Student ID Card"
                              className="w-16 h-12 object-cover rounded-xl border border-gray-200 shadow-xs flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <span className="font-gilroyBold  text-gray-900 block truncate">
                                Student ID Card
                              </span>
                              <span className="font-gilroyBold text-xs text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full inline-block mt-1">
                                ✓ Current ID card verified
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => idCardInputRef.current?.click()}
                            className="text-xs font-gilroyBold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-xl border border-emerald-200 cursor-pointer transition-all flex-shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => idCardInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-200 hover:border-emerald-500 bg-white rounded-2xl p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-1.5"
                        >
                          <UploadCloud className="w-7 h-7 text-emerald-600 mb-0.5" />
                          <span className="font-gilroyBold text-base text-gray-800">
                            {uploadingIdCard ? "Uploading ID card..." : "Click to upload student ID card"}
                          </span>
                          <span className="text-xs text-gray-500">JPG or PNG up to 5MB</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleNextStep1}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-gilroyBold text-xl rounded-2xl shadow-lg shadow-emerald-600/25 border-0 cursor-pointer transition-all hover:scale-[1.01] flex items-center justify-center gap-2"
            >
              <span>Next: Social Media & Links</span>
              <ArrowRight size={22} />
            </button>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6 text-left">
          <div className="border-b border-gray-100 pb-3">
            <h2 className="font-gilroyBold text-2xl text-gray-900 m-0">
              Step 2: Social Profiles & Reach
            </h2>
            <p className="text-gray-500 text-[13px] m-0 mt-1">
              Tell us about your social media reach and top-performing posts.
            </p>
          </div>

          <div className="space-y-4">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              Social Media Profile URLs <span className="text-red-500">*</span> <br />
              <span className="text-gray-500 font-normal text-[13px]">
                (at least 1 link required, must start with https://)
              </span>
            </label>

            <div>
              <label className="block text-[13px] font-gilroyBold text-gray-700 mb-1">
                Instagram Profile URL
              </label>
              <Input
                size="large"
                placeholder="https://www.instagram.com/yourprofile"
                value={igUrl}
                onChange={(e) => setIgUrl(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-[13px] font-gilroyBold text-gray-700 mb-1">
                TikTok Profile URL
              </label>
              <Input
                size="large"
                placeholder="https://www.tiktok.com/@yourprofile"
                value={tiktokUrl}
                onChange={(e) => setTiktokUrl(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium"
              />
            </div>

            <div>
              <label className="block text-[13px] font-gilroyBold text-gray-700 mb-1">
                Facebook Profile URL
              </label>
              <Input
                size="large"
                placeholder="https://www.facebook.com/yourprofile"
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium"
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              Total Followers across ALL Platforms <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: "1,000 - 3,000", value: "1000-3000" },
                { label: "3,000 - 6,000", value: "3000-6000" },
                { label: "6,000+", value: "6000+" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFollowerRange(opt.value)}
                  className={`py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all text-center cursor-pointer ${followerRange === opt.value
                    ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                    : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="block text-lg font-gilroyBold text-gray-900 m-0">
                Links to your top 5 best posts <span className="text-red-500">*</span>
              </label>
              <span className="text-xs font-gilroyBold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full border border-emerald-200">
                {topPosts.filter((p) => p.trim() !== "").length} / 5
              </span>
            </div>

            <div className="space-y-3">
              {topPosts.map((link, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-[13px] font-gilroyBold text-gray-600 w-20 flex-shrink-0">
                    Post #{idx + 1}
                  </span>
                  <Input
                    size="large"
                    placeholder="https://..."
                    value={link}
                    onChange={(e) => handlePostLinkChange(idx, e.target.value)}
                    className="rounded-2xl h-14 text-base font-medium flex-1"
                  />
                  {topPosts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePostLink(idx)}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition border border-red-100 cursor-pointer"
                    >
                      <DeleteOutlined style={{ fontSize: "18px" }} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {topPosts.length < 5 && (
              <button
                type="button"
                onClick={handleAddPostLink}
                className="flex items-center gap-2 text-emerald-700 font-gilroyBold text-[13px] bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-4 py-2.5 rounded-2xl transition cursor-pointer"
              >
                <PlusOutlined /> Add Another Post Link
              </button>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-gilroyBold text-lg rounded-2xl transition cursor-pointer"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleNextStep2}
              className="w-2/3 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-gilroyBold text-lg rounded-2xl shadow-lg shadow-emerald-600/25 border-0 cursor-pointer transition-all hover:scale-[1.01] flex items-center justify-center gap-2"
            >
              <span>Next: Questions & Terms</span>
              <ArrowRight size={22} />
            </button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6 text-left">
          <div className="border-b border-gray-100 pb-3">
            <h2 className="font-gilroyBold text-2xl text-gray-900 m-0">
              Step 3: Eligibility & Agreement
            </h2>
            <p className="text-gray-500 text-[13px] m-0 mt-1">
              Answer a few final questions and accept the terms to submit your application.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              1. Do you have a strong social media presence?
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setHasSocialPresence(true)}
                className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all cursor-pointer ${hasSocialPresence === true
                  ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setHasSocialPresence(false)}
                className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all cursor-pointer ${hasSocialPresence === false
                  ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                No
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              2. How many Top Restaurants or Stores you can onboard monthly?
            </label>
            <div className="flex gap-3">
              {[
                { label: "1 - 5 businesses", value: "1-5" },
                { label: "5 & above businesses", value: "5+" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setOnboardRange(opt.value)}
                  className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all text-center cursor-pointer ${onboardRange === opt.value
                    ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              3. Do you have time to post daily promotional content?
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setHasTimeDaily(true)}
                className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all cursor-pointer ${hasTimeDaily === true
                  ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setHasTimeDaily(false)}
                className={`flex-1 py-3.5 rounded-2xl border-2 text-lg font-gilroyBold transition-all cursor-pointer ${hasTimeDaily === false
                  ? "border-emerald-600 bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                No
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4 flex flex-col items-center space-y-3">
            <button
              type="button"
              onClick={() => {
                setAgreedToTerms(true);
                if (onOpenTerms) onOpenTerms("overview");
              }}
              className="text-emerald-600 font-gilroyBold text-[13px] hover:text-emerald-700 transition-all cursor-pointer underline"
            >
              Click to read the Ambassador agreements & terms
            </button>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAgreedToTerms(checked);
                  if (checked && onOpenTerms) {
                    onOpenTerms("overview");
                  }
                }}
                className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
              />
              <span className="text-gray-800 text-[13px] font-gilroyBold">
                I agree to the Ambassador agreements & terms
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-gilroyBold text-lg rounded-2xl transition cursor-pointer"
            >
              Back
            </button>
            <button
              onClick={handleApply}
              disabled={submitting || (!hasExistingApplication && !agreedToTerms)}
              className={`w-2/3 uppercase text-white flex items-center justify-center rounded-2xl font-gilroyBold text-xl transition-all py-4 px-6 shadow-xl ${submitting || (!hasExistingApplication && !agreedToTerms)
                ? "bg-gray-300 cursor-not-allowed shadow-none"
                : "bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/30 hover:scale-[1.02] cursor-pointer"
                }`}
            >
              <span>{submitting ? "SAVING..." : (hasExistingApplication ? "SAVE CHANGES" : "APPLY NOW")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbassadorApplicationForm;
